import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'

interface SEOHeadProps {
  title: string
  description: string
  keywords?: string[]
  canonical?: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'product'
  noIndex?: boolean
  structuredData?: Record<string, any>
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords = [],
  canonical,
  ogImage = 'https://zentriz.com.br/icons/og-image.png',
  ogType = 'website',
  noIndex = false,
  structuredData
}) => {
  const { i18n } = useTranslation()
  const currentUrl = canonical || `https://zentriz.com.br${window.location.pathname}`
  
  // Gerar hreflang tags para internacionalização.
  // O site é uma SPA de URL única por rota (troca de idioma é client-side, mesma URL).
  // Emitir apenas alternates que resolvem de fato — pt-BR + x-default apontando para a mesma URL.
  // (Não há rotas com prefixo /en ou /es; anunciá-las geraria 404 no crawler.)
  const generateHreflang = () => {
    const baseUrl = 'https://zentriz.com.br'
    const pathname = window.location.pathname

    return (
      <>
        <link rel="alternate" hrefLang="pt-BR" href={`${baseUrl}${pathname}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${pathname}`} />
      </>
    )
  }
  
  // Palavras-chave base da Zentriz
  const baseKeywords = [
    'Zentriz',
    'tecnologia',
    'soluções tecnológicas',
    'consultoria tecnológica',
    'desenvolvimento de software',
    'cloud computing',
    'AWS',
    'Azure',
    'GCP',
    'React',
    'Node.js',
    'inteligência artificial',
    'IA',
    'machine learning',
    'transformação digital',
    'inovação empresarial',
    'arquitetura de software',
    'DevOps',
    'serverless',
    'microserviços'
  ]

  const allKeywords = [...baseKeywords, ...keywords].join(', ')

  return (
    <Helmet>
      {/* Título e descrição */}
      <title>{title} - Zentriz</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Hreflang para internacionalização */}
      {generateHreflang()}
      
      {/* Robots */}
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1'} />
      
      {/* Idioma e localização */}
      <meta name="language" content={i18n.language === 'pt' ? 'Portuguese' : 'English'} />
      <meta httpEquiv="content-language" content={i18n.language === 'pt' ? 'pt-BR' : 'en-US'} />
      
      {/* Open Graph */}
      <meta property="og:title" content={`${title} - Zentriz`} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Zentriz" />
      <meta property="og:locale" content={i18n.language === 'pt' ? 'pt_BR' : 'en_US'} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - Zentriz`} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@zentriz" />
      <meta name="twitter:creator" content="@zentriz" />
      <meta name="twitter:title" content={`${title} - Zentriz`} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={`${title} - Zentriz`} />
      
      {/* Dados estruturados */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* DNS Prefetch para recursos externos */}
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
    </Helmet>
  )
}

export default SEOHead
